<template>
  <v-flex
    :class="{
      'nx-list-item': true,
      'condensed' : condensed,
      'mosaic': mosaic,
      'bag': bag,
      'clipboard': clipboard,
      'selection': selection,
      'dark': dark,
      'title-one-line': titleOneLine,
      'title-two-lines': titleTwoLines,
      'title-three-lines': titleThreeLines,
      'cursor-pointer': selectable,
    }"
    :xs12="!mosaic"
    :xs6="mosaic"
    :sm4="mosaic"
    :lg3="mosaic"
    grow
  >
    <router-link
      :is="itemUrl ? 'router-link' : 'span'"
      :event="selectable || disabled ? '' : 'click'"
      :target="openInNewTab ? '_blank' : '_self'"
      :to="itemUrl"
      class="link--item"
      active-class="active"
      @click.stop
    >
      <v-layout
        v-ripple="selectable"
        :column="mosaic"
        fill-height
        class="list--item"
        @mouseover.passive="hovered = true"
        @mouseleave.passive="hovered = false"
      >
        <v-flex
          shrink
          @click="$emit('click:item')"
        >
          <v-layout
            align-center
            fill-height
          >
            <template v-if="bulkSelection">
              <template v-if="mosaic">
                <v-flex
                  align-self-start
                  class="checkbox--absolute"
                >
                  <v-checkbox
                    :ripple="false"
                    color="secondary"
                    hide-details
                    class="ma-0 mr-0 pa-0"
                    :input-value="checked"
                    :value="checked"
                    @click.stop
                    @change="$emit('input:check', $event)"
                  />
                </v-flex>
              </template>
              <template v-else>
                <v-flex
                  class="checkbox--area"
                  @click.capture.prevent.stop="$emit('input:check', !checked)"
                >
                  <v-checkbox
                    hide-details
                    class="my-0 mx-2 pa-0"
                    color="secondary"
                    :value="checked"
                    :ripple="false"
                    :input-value="checked"
                    @click.stop
                    @change="$emit('input:check', $event)"
                  />
                </v-flex>
              </template>
            </template>
            <nx-list-item-image
              class="nx-list-item-image"
              :image="computedImage"
              :icon="icon"
              :bag="bag"
              :mosaic="mosaic"
              :normal="normal"
              :selection="selection"
              :sortable="sortable"
              :grabbing="grabbing"
              :keep-image-aspect-ratio="keepImageAspectRatio"
              :hovered="hovered"
            />
          </v-layout>
        </v-flex>
        <v-flex
          :xs12="mosaic"
          class="text--item-content"
          :d-flex="clipboard"
          :align-center="clipboard"
          @click="$emit('click:item')"
        >
          <v-layout
            :fill-height="!clipboard"
            column
          >
            <v-flex
              :align-center="condensed || selection"
              :class="{'d-flex': selection}"
            >
              <h2 class="text--item-title">
                {{ title }}
              </h2>
              <h3
                v-if="subtitle && !condensed"
                class="text--item-subtitle caption mb-0"
              >
                {{ subtitle }}
              </h3>
            </v-flex>
            <v-flex
              v-show="showChips"
              class="text--item-chips hidden-xs-only"
              shrink
              align-self-baseline
            >
              <template v-for="(chip, index) in chips">
                <nx-list-item-chip
                  :key="`id-${index}`"
                  v-bind="chip"
                  v-on="$listeners"
                />
              </template>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          class="text--item-actions"
          :mb-2="!condensed"
          shrink
          @click.capture.prevent
        >
          <slot
            name="actions"
            :hovered="hovered"
            :metadata="metadata"
          />
        </v-flex>
        <nx-list-item-flag
          v-if="flag && !hideFlag"
          :color="flag.color"
          :tooltip="flag.tooltip"
        />
      </v-layout>
    </router-link>
  </v-flex>
</template>
<script>
import { entityEditorRoute } from '@/modules/entity/router';
import NxListItemFlag from '@/components/list/components/ListItemFlag';
import NxListItemChip from '@/components/list/components/ListItemChip';
import NxListItemImage from '@/components/list/components/ListItemImage';

export default {
  name: 'NxListItem',
  components: {
    NxListItemImage,
    NxListItemChip,
    NxListItemFlag,
  },
  props: {
    title: {
      type: String,
      default: undefined,
    },
    subtitle: {
      type: String,
      default: undefined,
    },
    bulkSelection: Boolean,
    chips: {
      type: Array,
      default: undefined,
    },
    image: {
      type: Object,
      default: undefined,
    },
    imageResolverFunction: {
      type: Function,
      default: undefined,
    },
    keepImageAspectRatio: Boolean,
    icon: {
      type: Object,
      default: undefined,
    },
    flag: {
      type: Object,
      default: undefined,
    },
    metadata: {
      type: [Object, Array],
      default: () => ({}),
    },
    type: {
      type: String,
      default: undefined,
    },
    id: {
      type: Number,
      default: undefined,
    },
    siteId: {
      type: Number,
      default: undefined,
    },
    selectable: Boolean,
    sortable: Boolean,
    grabbing: Boolean,
    checked: Boolean,
    hideFlag: Boolean,
    hideChips: Boolean,
    openInNewTab: Boolean,
    // Modos de visualización
    normal: Boolean,
    condensed: Boolean,
    mosaic: Boolean,
    bag: Boolean,
    selection: Boolean,
    clipboard: Boolean,
    disabled: Boolean,
    dark: Boolean,
    // Numero de lineas de título
    titleOneLine: Boolean,
    titleTwoLines: Boolean,
    titleThreeLines: Boolean,
    params: {
      type: [Object, Array],
      default: undefined,
    },
    url: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      hovered: false,
      lazyImage: null,
    };
  },
  computed: {
    computedImage() {
      if (this.image && this.image.src) {
        return this.image;
      }
      return this.lazyImage;
    },
    editorRoute() {
      const location = entityEditorRoute.get(this.type, {
        id: this.id,
        type: this.type,
        siteId: this.siteId,
      });

      const { route } = this.$router.resolve(location);
      if (!route.matched.length) {
        // Disable list item link on items with no entity editor
        return null;
      }
      return location;
    },
    itemUrl() {
      return this.editorRoute || null;
    },
    showChips() {
      if (this.hideChips || this.condensed) {
        return false;
      }
      return this.chips && this.chips.length > 0;
    },
  },
  watch: {
    id: {
      handler() {
        this.fetchImage();
      },
      immediate: true,
    },
  },
  methods: {
    async fetchImage() {
      if (!this.imageResolverFunction) {
        return;
      }
      this.lazyImage = null;
      if (this.image && this.image.src) {
        return;
      }
      this.lazyImage = await this.imageResolverFunction.call(this, this.$props);
    },
  },
};
</script>

<style lang="stylus">
@import '../../../assets/stylus/variables.styl'

.nx-list-item {
  height: 86px;
  margin-bottom: 4px;
  width: 100%;
  transform: translate3d(0, 0, 0);

  .v-image {
    background-color: #e9e9f2;
  }

  &.mosaic {
    height: 256px;
    padding-right: 5px;
    @media $xs-only {
      height: 230px;
    }

    .v-image {
      border-radius: 4px 4px 0 0;
    }
  }

  &.bag {
    height: 80px;
  }

  &.selection {
    height: 58px;
  }

  .list--item {
    background-color white
    position: relative;
    border-top: 1px solid $nx-neutral-100
    border-right: 1px solid $nx-neutral-100
    border-bottom: 1px solid $nx-neutral-100

    .dark& {
      background-color: $nx-neutral-50;
    }

    .bag& {
      background-color: $nx-primary--darken-2;
      border: none;
    }

    .mosaic& {
      border-radius: 4px;
    }

    .v-input--checkbox .v-input--selection-controls__input {
      margin: 0;
    }
  }

  .text--item {
    &-content {
      height: 100%;
      padding: 15px 15px 12px 15px;

      // Flex items have `min-width: auto` by default, causing them to overflow their parents.
      // @see https://defensivecss.dev/tip/flexbox-min-content-size/
      min-width: 0;

      @media $xs-only {
        padding: 9px 10px 8px;
      }

      .condensed& {
        padding: 10px 15px;
      }

      .mosaic& {
        padding: 10px;
      }

      .selection& {
        padding: 7px 10px;
      }

      .bag& {
        padding: 10px 0 10px 15px;
      }
    }

    &-chips {
      overflow: hidden;
      max-height: 20px;
    }

    &-actions {
      min-height: 36px;
      padding-top: 8px;
      padding-right: 15px;
      @media $xs-only {
        padding: 0;
      }

      .condensed& {
        padding-top: 2px;
      }

      .mosaic& {
        padding-top: 0;
      }

      .selection& {
        padding-right: 5px;
      }

      .bag& {
        padding: 0;
      }
    }

    &-title {
      color: black;
      font-size: 15px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.25;
      letter-spacing: normal;

      overflow: hidden;
      overflow-wrap: break-word;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;

      .mosaic& {
        font-size: 14px;
        -webkit-line-clamp: 2;
      }

      .bag& {
        color: $nx-primary--lighten-5;
        -webkit-line-clamp: 3;
      }

      .selection& {
        font-size: 13px;
        line-height: 1.15;
        color: #27272e;
        -webkit-line-clamp: 3;
      }

      @media $xs-only {
        display: -webkit-box;
        -webkit-line-clamp: 4;
        font-size: 14px;
        .condensed& {
          -webkit-line-clamp: 1;
        }
      }

      .title-one-line& {
        -webkit-line-clamp: 1 !important;
      }

      .title-two-lines& {
        -webkit-line-clamp: 2 !important;
      }

      .title-three-lines& {
        -webkit-line-clamp: 3 !important;
      }
    }

    &-subtitle {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .icon--item {
    background: #e9e9f2;

    .mosaic& {
      border-radius: 4px 4px 0 0;
    }

    .selection& {
      background: white;
      box-shadow: inset 0 1px 0 0 rgba(0, 0, 0, 0.06), inset 0 -1px 0 0 rgba(0, 0, 0, 0.12);
    }

    .bag& {
      background: $nx-primary--darken-1;
    }
  }

  .link--item {
    display: contents;
    text-decoration: none;
    color: inherit

    &.active {
      .text--item-title {
        color: $primary;
      }
    }
  }

  &.condensed {
    max-height: 40px;
  }

  .checkbox--area {
    height: 100%;

    .v-input {
      height: 100%;
      align-items: center;
    }
  }

  .checkbox--absolute {
    z-index: 1;
    position: absolute;
    top: 10px;
    left: 10px;

    .v-input--selection-controls__input {
      position: relative;

      &:after {
        pointer-events: none;
        content: '';
        display: block;
        position: absolute;
        background-color: white;
        top: .2em;
        right .2em;
        bottom: .2em;
        left .2em;
        border-radius: 2px;
      }

      input {
        z-index: 2;
      }

      .v-icon {
        z-index: 1;
      }
    }
  }

  i.fa.fa-google {
    margin-right: 4px;
    color: white;
  }

  &.sortable-ghost {
    opacity: 0.6
    filter: blur(1px);
  }
}
</style>
